import { useNavigationContainerRef } from 'expo-router/react-navigation';
import { useEffect } from 'react';
import { isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { getItem, setItem } from 'shared/utils/asyncStorage';

/**
 * Custom hook to track and persist web referrer and navigation paths.
 *
 * On first load, it sets:
 * PREVIOUS_PATH = document.referrer (or current location if no referrer)
 * CURRENT_PATH = current window location
 *
 * On subsequent navigation changes (using React Navigation),
 * it updates the stored CURRENT_PATH and shifts it to PREVIOUS_PATH.
 *
 */
const useWebReferrer = () => {
  const navigationRef = useNavigationContainerRef();

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const setupInitialPaths = async () => {
      const prev = await getItem(STORAGE_KEYS.PREVIOUS_PATH);
      const current = await getItem(STORAGE_KEYS.CURRENT_PATH);

      // On first app load, initialize referrer and current path
      if (!prev && !current) {
        const referrer = document.referrer || window.location.href;
        await setItem(STORAGE_KEYS.PREVIOUS_PATH, referrer);
        await setItem(STORAGE_KEYS.CURRENT_PATH, window.location.href);
      }
    };

    // Initialize stored paths
    void setupInitialPaths();

    // Listen for navigation state changes to update previous/current paths
    const unsubscribe = navigationRef.addListener?.('state', async () => {
      const prev = await getItem(STORAGE_KEYS.CURRENT_PATH);
      if (prev) {
        await setItem(STORAGE_KEYS.PREVIOUS_PATH, prev);
      }

      await setItem(STORAGE_KEYS.CURRENT_PATH, window.location.href);
    });

    // Cleanup the listener on unmount
    return () => {
      unsubscribe?.();
    };
  }, [navigationRef]);
};

export default useWebReferrer;
